import type { Tone } from '../../components/ui/primitives/cx';
import type { ReportMeta, ReportVariation } from '../../types/reports';
import { formatMoney } from '../ownerOverview';
import { rangePhrase, rangeText, type ReportContext, type Segment } from '../reportsView';

/**
 * P-084 · Lo que comparten las vistas puras de los cinco reportes (sin React, testeable en node): la forma de
 * las piezas que los componentes dibujan, el contexto que sale de `meta`, y el formato de lo que el servidor ya
 * calculó. Regla heredada de R1/R2: ninguna cifra de dinero se calcula aquí; los importes llegan hechos y solo se
 * les da formato. Lo que no se conoce se ve como desconocido («—»), nunca como cero.
 */

export const UNKNOWN = '—';
export const MIXED_CURRENCY_NOTE = 'Hay ventas en más de una moneda: los importes no se suman. Las cantidades sí.';
export const SCOPED_NOTE = 'Solo tus tiendas: los gastos y compras generales del negocio no están incluidos.';
export const MIXED_TIMEZONES_NOTE = 'Tus tiendas están en horarios distintos. Cada una cuenta su propio día y sus propias horas.';
export const APPROXIMATE_NOTE = 'Parte del costo se basa en valores aproximados.';
export const NO_COMPARISON_NOTE = 'Todavía no tengo con qué comparar. En unas semanas te podré decir si vas mejor o peor.';

export type StatTone = 'default' | 'muted' | 'danger' | 'success' | 'warning';

export interface StatView {
  key: string;
  label: string;
  /** Ya formateado; «—» cuando no se conoce. */
  value: string;
  hint: string | null;
  tone: StatTone;
}

export interface BarView {
  key: string;
  label: string;
  /** La cifra de la barra, en palabras («48 operaciones · $5,120.00»). */
  valueLabel: string;
  /** Ancho relativo a la mayor de la lista, 0–100. Es dibujo: la cifra va en el texto. */
  percent: number;
  highlighted: boolean;
}

export interface BadgeView { text: string; tone: 'neutral' | Tone }

export interface RowView {
  key: string;
  title: string;
  subtitle: string | null;
  meta: string[];
  value: string | null;
  valueTone: StatTone;
  badge: BadgeView | null;
  figures: Array<{ label: string; value: string }>;
  /** La sección de la app que lo resuelve (`onNavigate`), o null. */
  destination: string | null;
}

export interface SectionView {
  key: string;
  title: string;
  rows: RowView[];
  /** Lo que se dice cuando no hay renglones, en vez de una lista vacía. */
  empty: string | null;
  note: string | null;
  /** «N más en …», cuando el servidor acotó la lista. */
  more: string | null;
}

export interface CoverageBarView {
  knownPercent: number;
  knownLabel: string;
  missingLabel: string;
}

export const upperFirst = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
export const plural = (count: number, one: string, many: string) => (count === 1 ? one : many);

/** Un entero en palabras del lector: «1,250». */
export function intLabel(value: number): string {
  return value.toLocaleString('es-MX', { maximumFractionDigits: 3 });
}

/** Un importe del servidor, o «—» cuando no se conoce. */
export function moneyLabel(label: string | null): string {
  return label === null ? UNKNOWN : formatMoney(label);
}

/** Un costo unitario del servidor (cuatro decimales): se enseña con los decimales que necesite, hasta cuatro. */
export function unitCostLabel(label: string): string {
  const value = Number(label);
  const decimals = Math.abs(value) >= 1 ? 2 : 4;
  return `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: decimals })}`;
}

export function percentLabel(value: number | null): string {
  return value === null ? UNKNOWN : `${Math.round(value)} %`;
}

/** 13 → «1 PM», 0 → «12 AM», 24 → «12 AM». */
export function hourLabel(hour: number): string {
  const normalized = ((hour % 24) + 24) % 24;
  const twelve = normalized % 12 === 0 ? 12 : normalized % 12;
  return `${twelve} ${normalized < 12 ? 'AM' : 'PM'}`;
}

/** «de 5 a 8 PM» cuando comparten AM/PM; si no, «de 11 AM a 2 PM». */
export function windowLabel(startHour: number, endHour: number): string {
  const start = ((startHour % 24) + 24) % 24;
  const end = ((endHour % 24) + 24) % 24;
  const startMeridiem = start < 12 ? 'AM' : 'PM';
  const endMeridiem = end < 12 || end === 0 ? (end === 0 ? 'AM' : 'AM') : 'PM';
  const twelve = (hour: number) => (hour % 12 === 0 ? 12 : hour % 12);
  if (startMeridiem === endMeridiem) return `de ${twelve(start)} a ${twelve(end)} ${endMeridiem}`;
  return `de ${hourLabel(start)} a ${hourLabel(end)}`;
}

/** «12 % más», «$500.00 menos», «Igual que antes». La flecha va aparte y es adorno. */
/**
 * Un cambio contra el periodo anterior, en palabras. `percent` cae a pesos cuando no hay porcentaje (base cero o < 1 %);
 * `percent-count` cae a un conteo (R2-10: una diferencia de operaciones nunca se dice en pesos).
 */
export function variationText(variation: ReportVariation, mode: 'percent' | 'percent-count' | 'money' | 'count'): { direction: 'up' | 'down' | 'flat'; text: string } {
  const amount = Number(variation.amount);
  if (amount === 0) return { direction: 'flat', text: 'Igual que antes' };
  const word = amount > 0 ? 'más' : 'menos';
  const rounded = variation.percentage === null ? 0 : Math.round(Math.abs(variation.percentage));
  const magnitude = (mode === 'percent' || mode === 'percent-count') && rounded >= 1 ? `${rounded} %` : mode === 'count' || mode === 'percent-count' ? intLabel(Math.abs(amount)) : formatMoney(Math.abs(amount));
  return { direction: amount > 0 ? 'up' : 'down', text: `${magnitude} ${word}` };
}

/** El periodo, el alcance y sus notas, a partir de la meta de un reporte (la misma pieza que R1/R2 dibujan). */
export function reportMetaContext(meta: ReportMeta, today: string): ReportContext {
  const notes: string[] = [];
  if (meta.scope.excludesBusinessWideRecords) notes.push(SCOPED_NOTE);
  if (meta.mixedTimezones) notes.push(MIXED_TIMEZONES_NOTE);
  if (meta.currency === null) notes.push(MIXED_CURRENCY_NOTE);
  const canCompare = meta.history.canCompare && meta.period.previousFromIso !== null && meta.period.previousToIso !== null;
  return {
    periodLabel: upperFirst(rangePhrase(meta.period.fromIso, meta.period.toIso, today)),
    scopeLabel: meta.scope.label,
    scopeNotes: notes,
    comparedWith: canCompare ? rangePhrase(meta.period.previousFromIso!, meta.period.previousToIso!, today) : null,
  };
}

/** «el 5 de octubre», como complemento de una frase. */
export function dayPhrase(iso: string, today: string): string {
  return `el ${rangeText(iso, iso, today)}`;
}

export const text = (value: string): Segment => ({ text: value });
export const strong = (value: string, amount = false): Segment => ({ text: value, strong: true, amount });
